import * as React from 'react';

import { LinearProgress } from '@mui/material';

import { InfiniteQueryObserverBaseResult } from '@tanstack/react-query';
import { MaterialReactTable, useMaterialReactTable } from 'material-react-table';

import { VenueReservationContentItemV2 } from '../../../graphql/API';
import { type ContentTab, useContentColumns } from './Column';

type ContentTableProps = {
  data: VenueReservationContentItemV2[];
  contentTab: ContentTab;
  onRowClick: (item: VenueReservationContentItemV2) => void;
  onDownloadClick?: (item: VenueReservationContentItemV2) => void;
} & Pick<
  InfiniteQueryObserverBaseResult,
  'isFetching' | 'isLoading' | 'fetchNextPage' | 'hasNextPage'
>;

export const ContentTable: React.FC<ContentTableProps> = ({
  data,
  contentTab,
  isFetching,
  isLoading,
  fetchNextPage,
  hasNextPage,
  onRowClick,
  onDownloadClick,
}) => {
  const fetchMoreOnBottomReached = React.useCallback(
    (containerRefElement?: HTMLDivElement | null) => {
      if (containerRefElement) {
        const { scrollHeight, scrollTop, clientHeight } = containerRefElement;
        if (scrollHeight - scrollTop - clientHeight < 500 && !isFetching && hasNextPage) {
          fetchNextPage();
        }
      }
    },
    [fetchNextPage, hasNextPage, isFetching],
  );

  const renderBottomToolbar = React.useCallback(
    () => !isLoading && isFetching && <LinearProgress />,
    [isLoading, isFetching],
  );

  const columns = useContentColumns({
    contentTab,
    onViewClick: onRowClick,
    onDownloadClick,
  });

  const table = useMaterialReactTable({
    enableRowSelection: false,
    enableStickyHeader: true,
    enableTopToolbar: false,
    enableBottomToolbar: false,
    enableColumnActions: false,
    enableColumnFilters: false,
    enablePagination: false,
    enableSorting: false,
    muiTableHeadProps: { sx: { opacity: 1 } },
    muiTableHeadRowProps: {
      sx: { backgroundColor: 'white', boxShadow: 'none' },
    },
    muiTableBodyRowProps: ({ row }) => ({
      onClick: () => onRowClick(row.original),
      sx: {
        backgroundColor: 'inherit',
        cursor: 'pointer',
      },
    }),
    muiTableBodyCellProps: {
      sx: ({ custom }) => ({ borderColor: custom.colors.brandLightGrey }),
    },
    muiTableHeadCellProps: {
      sx: ({ custom }) => ({
        color: 'text.secondary',
        borderColor: custom.colors.brandLightGrey,
        fontWeight: 400,
      }),
    },
    muiTablePaperProps: { sx: { boxShadow: 'none' } },
    muiTableContainerProps: {
      sx: {
        height: 'calc(100vh - 300px)',
        maxHeight: 'calc(100vh - 300px)',
        backgroundColor: 'white',
      },
      onScroll: (event) => fetchMoreOnBottomReached(event.target as HTMLDivElement),
    },
    renderBottomToolbar,
    columns,
    data,
    rowVirtualizerOptions: { overscan: 5 },
    state: { isLoading },
    getRowId: (row) => String(row.id),
  });

  return <MaterialReactTable table={table} />;
};
